/**
 * Les missions d'assistance technique que le PEV reçoit, et l'agent prévu pour
 * chacune. Repris de la note de cadrage d'Efficience Globale (« Agents IA et
 * skills pour le PEV Guinée », version 1, octobre 2026).
 *
 * Aucune n'est encore disponible : ce qui existe aujourd'hui sur la
 * plateforme ne couvre pas ce que leurs fiches exigent. Le statut passera à
 * « disponible » mission par mission, quand l'agent correspondant sera prêt.
 */
export type Niveau = 'Remplace' | 'Augmente' | 'Humain';

export interface MissionAT {
  code: string;
  titre: string;
  mission: string;
  profil: string;
  change: string;
  vague: string;
  disponible: boolean;
  fonctionnement: string;
  taches: { tache: string; habituel: string; avec_agent: string; niveau: Niveau }[];
  utilisateurs: string;
  sources: string[];
  livrables: string[];
  skills: string;
  garde_fous: string[];
  prerequis: string[];
}

export const MISSIONS_AT: MissionAT[] = [
  {
    "code": "A1",
    "titre": "Demande de financement Gavi",
    "fonctionnement": "Le consultant arrive après la mise en place du comité de pilotage. Il conduit la revue documentaire, anime les groupes thématiques, rédige l'analyse situationnelle, la théorie du changement et le narratif, puis consolide le budget et le cadre de résultats avec les partenaires. La cohérence entre narratif, budget et cadre de résultats se vérifie à la main, en fin de processus, sous contrainte de délai. Quand l'IRC renvoie des commentaires, le pays doit remobiliser un appui.",
    "taches": [
      {
        "tache": "Revue documentaire et analyse situationnelle",
        "habituel": "Lecture manuelle des documents par le consultant et les groupes thématiques",
        "avec_agent": "L'agent remplit la grille Gavi question par question à partir des documents du PEV et indique la source de chaque élément",
        "niveau": "Remplace"
      },
      {
        "tache": "Priorisation des obstacles",
        "habituel": "Atelier, application des 9 critères OMS",
        "avec_agent": "L'agent pré-classe les obstacles selon les 9 critères et documente chaque score pour la discussion en atelier",
        "niveau": "Augmente"
      },
      {
        "tache": "Théorie du changement et narratif",
        "habituel": "Rédaction par le consultant",
        "avec_agent": "L'agent rédige les premiers jets section par section, selon le formulaire en vigueur",
        "niveau": "Remplace"
      },
      {
        "tache": "Budget et cadre de résultats",
        "habituel": "Consolidation sur tableur, vérifications ponctuelles",
        "avec_agent": "L'agent vérifie que chaque activité budgétée renvoie à un objectif, un indicateur et un passage du narratif, et liste les écarts",
        "niveau": "Augmente"
      },
      {
        "tache": "Contrôle de conformité avant soumission",
        "habituel": "Relecture finale, souvent tardive",
        "avec_agent": "L'agent applique la liste des exigences des lignes directrices et produit un rapport d'écarts",
        "niveau": "Remplace"
      },
      {
        "tache": "Réponse aux commentaires de l'IRC",
        "habituel": "Nouvelle mission ou appui à distance",
        "avec_agent": "L'agent produit le tableau de réponse point par point et met à jour les sections concernées de façon coordonnée",
        "niveau": "Augmente"
      },
      {
        "tache": "Dialogue pays, arbitrages, négociation avec Gavi",
        "habituel": "Consultant et coordination nationale",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Coordination nationale du PEV, secrétariat technique de la demande, présidents et rapporteurs des commissions thématiques.",
    "sources": [
      "Lignes directrices et formulaires Gavi en vigueur, chargés avec leur numéro de version",
      "FPP 2025-2029 : analyse situationnelle et argumentaire stratégique",
      "SNV 2024-2028, ENCV 2024, estimations WUENIC",
      "Rapports annuels, rapports de supervision et rapports TCA du PEV"
    ],
    "livrables": [
      "Analyse situationnelle pré-remplie avec références",
      "Premiers jets des sections narratives",
      "Rapport de cohérence narratif, budget et cadre de résultats",
      "Rapport de conformité avant soumission",
      "Tableau de réponse aux commentaires de l'IRC"
    ],
    "skills": "S1 Analyse situationnelle Gavi ; S2 Contrôle de cohérence chiffrée ; S3 Costing par activité ; S8 Ciblage des enfants zéro dose.",
    "garde_fous": [
      "Chaque chiffre cité renvoie à une source datée ; l'agent signale une donnée manquante au lieu de l'estimer",
      "Les lignes directrices utilisées sont identifiées par version ; un changement de version déclenche une nouvelle vérification",
      "Aucun document ne part vers Gavi sans validation de la coordination nationale"
    ],
    "prerequis": [
      "Formulaires et lignes directrices Gavi 6.0 dans leur version officielle",
      "Base documentaire du PEV indexée (réutilisation de l'index Omnia)"
    ],
    "mission": "Appui à l'élaboration des demandes de financement Gavi : FPP, demande Gavi 6.0, FAE, réponses aux commentaires de l'IRC, reprogrammations.",
    "profil": "Consultant international, appuyé par l'OMS et l'UNICEF, aux côtés du secrétariat technique national.",
    "change": "Le consultant arrive sur une analyse situationnelle pré-remplie et des premiers jets. Il consacre son temps à la priorisation et au dialogue pays.",
    "vague": "Vague 2",
    "disponible": false
  },
  {
    "code": "A2",
    "titre": "Planification stratégique et revues",
    "fonctionnement": "Le PEV met en place un comité de pilotage et des groupes thématiques : sept groupes pour la SNV 2024-2028. Les consultants consolident l'analyse, rédigent la stratégie et construisent le coût par activité à partir des TDR et des factures pro forma des années antérieures. Pour la revue externe, une équipe d'experts combine une enquête nationale de couverture, une évaluation GEV et une analyse institutionnelle, financière et opérationnelle.",
    "taches": [
      {
        "tache": "Analyse de situation par groupe thématique",
        "habituel": "Rédaction par chaque groupe, consolidation par le consultant",
        "avec_agent": "L'agent remet à chaque groupe une analyse pré-rédigée à partir des données et des rapports, dans un format commun",
        "niveau": "Augmente"
      },
      {
        "tache": "Costing par activité",
        "habituel": "Tableur construit par le consultant",
        "avec_agent": "L'agent applique la méthode de la SNV (quantité, facteur multiplicateur, coût unitaire), avec année de base et taux de change documentés, et calcule le gap de financement",
        "niveau": "Remplace"
      },
      {
        "tache": "Plan d'action annuel",
        "habituel": "Atelier de planification",
        "avec_agent": "L'agent décline la stratégie en plan annuel à partir du bilan de l'année précédente",
        "niveau": "Augmente"
      },
      {
        "tache": "Revue à mi-parcours",
        "habituel": "Atelier bilan",
        "avec_agent": "L'agent produit le tableau de bord des indicateurs du cadre de suivi-évaluation et l'analyse des écarts",
        "niveau": "Remplace"
      },
      {
        "tache": "Revue externe",
        "habituel": "Équipe d'experts nationaux et internationaux",
        "avec_agent": "L'agent prépare la revue documentaire et le dossier de données de la mission",
        "niveau": "Augmente"
      },
      {
        "tache": "Entretiens, jugement évaluatif, validation politique",
        "habituel": "Experts, comité de pilotage, CCIA",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Coordination nationale, Bureau de stratégie et de développement, groupes thématiques, équipe de revue.",
    "sources": [
      "SNV 2024-2028 et son budget (454,2 millions USD sur cinq ans, gap estimé à 165,5 millions USD)",
      "Plans d'action annuels et rapports de revue",
      "DHIS2, ENCV 2024, estimations WUENIC, rapports financiers"
    ],
    "livrables": [
      "Analyses thématiques pré-rédigées",
      "Matrice de costing et calcul du gap",
      "Projet de plan d'action annuel",
      "Tableau de bord des indicateurs et analyse des écarts",
      "Dossier documentaire et de données pour la revue externe"
    ],
    "skills": "S2 Contrôle de cohérence chiffrée ; S3 Costing par activité ; S13 Bulletin de performance ; S22 Fiches de poste, TDR et plans de formation.",
    "garde_fous": [
      "Chaque coût unitaire renvoie à sa pièce justificative (TDR, pro forma)",
      "Les hypothèses de change et d'année de base sont affichées dans chaque livrable"
    ],
    "prerequis": [
      "Fichier de costing de la SNV 2024-2028 et son format"
    ],
    "mission": "Élaboration et costing de la stratégie nationale de vaccination, plans d'action annuels, revue à mi-parcours, revue externe du programme.",
    "profil": "Consultants nationaux OMS et UNICEF pour la stratégie et son costing ; experts nationaux et internationaux pour la revue externe.",
    "change": "Le costing et l'analyse des indicateurs sont prêts. Les experts de la revue se concentrent sur les entretiens et le jugement évaluatif.",
    "vague": "Vague 3",
    "disponible": false
  },
  {
    "code": "A3",
    "titre": "Introduction de nouveaux vaccins",
    "fonctionnement": "Un consultant rédige le plan à partir du modèle OMS, calcule les cibles et les besoins, prépare le budget et le chronogramme, puis appuie les formations. Après son départ, les questions des districts remontent au niveau central et attendent une réponse. L'évaluation post-introduction mobilise une nouvelle équipe, plusieurs mois plus tard.",
    "taches": [
      {
        "tache": "Rédaction du plan d'introduction",
        "habituel": "Consultant",
        "avec_agent": "L'agent produit le plan complet au format attendu : justification, cibles, besoins, budget, chronogramme, formation, communication, suivi",
        "niveau": "Remplace"
      },
      {
        "tache": "Calcul des cibles et des besoins",
        "habituel": "Tableur",
        "avec_agent": "L'agent appelle le calcul selon le Module 6 OMS sur la population opérationnelle",
        "niveau": "Remplace"
      },
      {
        "tache": "Liste de vérification de la préparation",
        "habituel": "Suivi en réunion",
        "avec_agent": "L'agent tient la liste à jour par district et signale les retards à la coordination",
        "niveau": "Augmente"
      },
      {
        "tache": "Questions des CDI et des agents de santé",
        "habituel": "Remontée au central, réponse différée",
        "avec_agent": "L'agent répond à partir des seuls documents validés et transmet au central les questions sans réponse documentée",
        "niveau": "Remplace"
      },
      {
        "tache": "Évaluation post-introduction",
        "habituel": "Nouvelle mission",
        "avec_agent": "L'agent prépare les questionnaires et le plan d'analyse, puis analyse les données collectées",
        "niveau": "Augmente"
      },
      {
        "tache": "Choix de l'âge, des zones et du phasage ; gestion des incidents",
        "habituel": "Experts, GTCV, coordination",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Coordination nationale, section prestation de services, CRI, CDI, agents de santé (module questions-réponses).",
    "sources": [
      "Normes et procédures du PEV (2021)",
      "Guides OMS d'introduction des vaccins",
      "Documents de l'introduction du vaccin antipaludique",
      "Population opérationnelle et outil de quantification"
    ],
    "livrables": [
      "Plan d'introduction au format attendu",
      "Liste de vérification de la préparation par district",
      "Foire aux questions validée",
      "Questionnaires et rapport d'évaluation post-introduction"
    ],
    "skills": "S4 Plan d'introduction ; S5 Évaluation post-introduction ; S9 Calcul des besoins Module 6 ; S23 Population opérationnelle.",
    "garde_fous": [
      "L'agent cite le document et la page de chaque réponse donnée au terrain",
      "Aucun avis clinique individuel ; tout cas de MAPI est renvoyé vers le circuit national",
      "Les choix stratégiques restent marqués « à arbitrer » tant que le GTCV et la coordination ne les ont pas validés"
    ],
    "prerequis": [
      "Liste des introductions et switches planifiés, avec leurs dates"
    ],
    "mission": "Élaboration du plan d'introduction ou de changement de présentation (switch) d'un vaccin, préparation de l'introduction, évaluation post-introduction.",
    "profil": "Consultants mobilisés pour le plan d'introduction, OMS et UNICEF ; EG pour le plan d'introduction du vaccin antipaludique.",
    "change": "Le plan sort au format attendu ; l'appui externe porte sur les choix stratégiques. Les districts gardent un point de réponse après l'introduction.",
    "vague": "Vague 2",
    "disponible": false
  },
  {
    "code": "A4",
    "titre": "Microplanification",
    "fonctionnement": "Des ateliers régionaux ou de district réunissent les CDI et les chefs de centres de santé autour d'un consultant. Les microplans sont produits pendant l'atelier, sur papier ou sur tableur, avec des cibles tirées de sources différentes (INS, PNLP, dénombrements). Entre deux ateliers, les microplans sont rarement mis à jour.",
    "taches": [
      {
        "tache": "Calcul des cibles par aire de santé",
        "habituel": "Saisie manuelle, sources variables",
        "avec_agent": "L'agent applique la population opérationnelle et affiche l'écart avec les chiffres INS et PNLP",
        "niveau": "Remplace"
      },
      {
        "tache": "Recensement des sites et des stratégies (fixe, avancée, mobile)",
        "habituel": "Atelier",
        "avec_agent": "L'agent guide le CDI pas à pas et vérifie la cohérence entre sites, distances et cibles",
        "niveau": "Augmente"
      },
      {
        "tache": "Priorisation des aires à enfants zéro dose",
        "habituel": "Analyse par le consultant",
        "avec_agent": "L'agent applique la méthode de ciblage du FPP aux données les plus récentes",
        "niveau": "Remplace"
      },
      {
        "tache": "Besoins en vaccins et intrants",
        "habituel": "Calcul manuel",
        "avec_agent": "L'agent appelle le calcul selon le Module 6 OMS",
        "niveau": "Remplace"
      },
      {
        "tache": "Consolidation district et national",
        "habituel": "Compilation des fichiers",
        "avec_agent": "L'agent consolide les microplans et produit un rapport d'incohérences",
        "niveau": "Remplace"
      },
      {
        "tache": "Microplan de campagne",
        "habituel": "Équipes OMS et UNICEF",
        "avec_agent": "L'agent remplit le modèle de microplan de campagne : équipes, cibles, intrants, budget",
        "niveau": "Augmente"
      },
      {
        "tache": "Connaissance du terrain, négociation avec les communautés",
        "habituel": "CDI, chefs de centres de santé",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "CDI, CRI, chefs de centres de santé, section prestation de services.",
    "sources": [
      "Population_INS_PNLP_et_Microplan.xlsx (base opérationnelle)",
      "Données sur les zones spéciales et données populationnelles opérationnelles du PEV",
      "Analyse situationnelle FPP : cibles ajustées par district",
      "DHIS2, normes et procédures du PEV, modèle de microplan JNV polio 2025"
    ],
    "livrables": [
      "Microplans par aire de santé et par district",
      "Rapport de cohérence des microplans",
      "Liste priorisée des aires de santé à enfants zéro dose",
      "Microplans de campagne"
    ],
    "skills": "S6 Microplan de routine ; S7 Microplan de campagne ; S8 Ciblage des enfants zéro dose ; S9 Calcul des besoins Module 6 ; S23 Population opérationnelle.",
    "garde_fous": [
      "L'agent ne modifie aucune cible sans afficher la source et l'écart",
      "Le CDI valide chaque microplan avant consolidation"
    ],
    "prerequis": [
      "Population opérationnelle disponible au niveau de l'aire de santé (format à confirmer)",
      "La carte sanitaire du SNIS se limite aux 447 centres de santé (argumentaire FPP) : les postes de santé devront être saisis par le CDI"
    ],
    "mission": "Microplanification de la vaccination de routine, micro-plans d'équité par aire de santé, microplans de campagne.",
    "profil": "Consultant international mobilisé pour appuyer l'élaboration des microplans ; OMS et UNICEF pour les campagnes.",
    "change": "Le CDI produit et met à jour son microplan sans attendre l'atelier. L'atelier sert à valider et à arbitrer.",
    "vague": "Vague 1",
    "disponible": false
  },
  {
    "code": "A5",
    "titre": "Forecast et approvisionnement",
    "fonctionnement": "L'UNICEF produit ou accompagne les prévisions annuelles avec la section logistique, en vue des commandes et du cofinancement. Les hypothèses de pertes, de couverture et de stock de sécurité, ainsi que la population retenue, changent parfois d'un exercice à l'autre sans trace écrite. Le suivi des stocks se fait dans E-SIGL, au cas par cas.",
    "taches": [
      {
        "tache": "Prévisions annuelles et pluriannuelles",
        "habituel": "Expert UNICEF, tableur",
        "avec_agent": "L'agent calcule les besoins par antigène selon le Module 6 OMS, sur la population opérationnelle, avec une note d'hypothèses",
        "niveau": "Remplace"
      },
      {
        "tache": "Scénarios (introduction, campagne, variation de couverture)",
        "habituel": "Calcul ponctuel",
        "avec_agent": "L'agent simule plusieurs scénarios et compare leurs effets sur les besoins",
        "niveau": "Augmente"
      },
      {
        "tache": "Besoins de cofinancement",
        "habituel": "Calcul séparé",
        "avec_agent": "L'agent dérive la part de cofinancement à partir des prévisions",
        "niveau": "Augmente"
      },
      {
        "tache": "Plan d'approvisionnement et calendrier des livraisons",
        "habituel": "Tableur",
        "avec_agent": "L'agent propose un calendrier selon les stocks et les capacités de stockage",
        "niveau": "Augmente"
      },
      {
        "tache": "Suivi des stocks",
        "habituel": "Lecture d'E-SIGL au cas par cas",
        "avec_agent": "L'agent produit chaque semaine les alertes de rupture, de surstock et de péremption, et calcule les taux de perte",
        "niveau": "Remplace"
      },
      {
        "tache": "Rapprochement doses utilisées et enfants vaccinés",
        "habituel": "Rare",
        "avec_agent": "L'agent croise E-SIGL et DHIS2 par district",
        "niveau": "Remplace"
      },
      {
        "tache": "Commandes, redéploiements, validation des prévisions",
        "habituel": "Section logistique, UNICEF",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Section logistique, coordination nationale, gestionnaires de dépôts régionaux et préfectoraux.",
    "sources": [
      "Outil Excel de quantification du PEV : 14 antigènes, six feuilles, Module 6 OMS, base opérationnelle, croissance de 2,7 %",
      "E-SIGL et DHIS2",
      "Calendrier vaccinal et capacités de stockage"
    ],
    "livrables": [
      "Fichier de prévisions et note d'hypothèses",
      "Besoins de cofinancement",
      "Plan d'approvisionnement",
      "Bulletin hebdomadaire des alertes de stock"
    ],
    "skills": "S9 Calcul des besoins Module 6 ; S12 Contrôles de qualité des données ; S23 Population opérationnelle.",
    "garde_fous": [
      "Toute rupture de série dans E-SIGL est signalée, jamais extrapolée",
      "Les taux de cofinancement appliqués sont ceux confirmés par Gavi pour l'année concernée"
    ],
    "prerequis": [
      "Accès à E-SIGL par API ou par export régulier"
    ],
    "mission": "Prévisions des besoins en vaccins et intrants (forecast), besoins de cofinancement, plan d'approvisionnement, suivi des stocks.",
    "profil": "Experts UNICEF en prévision, quantification et approvisionnement, avec la section logistique du PEV.",
    "change": "Les prévisions se recalculent à tout moment avec des hypothèses tracées. L'expert UNICEF valide au lieu de calculer.",
    "vague": "Vague 1",
    "disponible": false
  },
  {
    "code": "A6",
    "titre": "Chaîne du froid et GEV",
    "fonctionnement": "Des experts consolident l'inventaire, définissent les besoins par site selon les critères de la plateforme et rédigent le plan de déploiement. La GEV fait l'objet d'une évaluation périodique, suivie d'un plan d'amélioration dont le suivi faiblit entre deux évaluations.",
    "taches": [
      {
        "tache": "Inventaire des équipements",
        "habituel": "Collecte et compilation manuelles",
        "avec_agent": "L'agent consolide l'inventaire et repère doublons et données manquantes",
        "niveau": "Remplace"
      },
      {
        "tache": "Analyse des besoins et plan de remplacement",
        "habituel": "Expert",
        "avec_agent": "L'agent classe les équipements par âge, état et capacité, et propose un plan de remplacement",
        "niveau": "Augmente"
      },
      {
        "tache": "Plan de déploiement CCEOP",
        "habituel": "Rédaction par l'expert",
        "avec_agent": "L'agent rédige le plan au format attendu à partir de l'inventaire et des priorités retenues",
        "niveau": "Augmente"
      },
      {
        "tache": "Suivi du plan d'amélioration GEV",
        "habituel": "Suivi irrégulier",
        "avec_agent": "L'agent tient le tableau de suivi et prépare les relances",
        "niveau": "Remplace"
      },
      {
        "tache": "Évaluation GEV sur site, choix techniques, réception des équipements",
        "habituel": "Experts, section logistique",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Section logistique, techniciens de maintenance, coordination nationale.",
    "sources": [
      "Inventaire de la chaîne du froid",
      "Rapports et plans d'amélioration GEV",
      "Manuel de procédures de la comptabilité matières",
      "Documentation POECF de la soumission FPP"
    ],
    "livrables": [
      "Inventaire consolidé",
      "Plan de remplacement",
      "Projet de plan de déploiement CCEOP",
      "Tableau de suivi du plan d'amélioration GEV"
    ],
    "skills": "S10 Inventaire et plan de remplacement ; S11 Suivi du plan GEV.",
    "garde_fous": [
      "Les choix d'équipements restent soumis aux critères techniques de la plateforme et à la validation de la section logistique"
    ],
    "prerequis": [
      "Format et date du dernier inventaire de la chaîne du froid",
      "Profil international de la mission : pratique courante, non documentée dans les fichiers du projet"
    ],
    "mission": "Plan de déploiement de la plateforme d'optimisation des équipements de la chaîne du froid (CCEOP), inventaire, plan de remplacement, auto-évaluation GEV et suivi du plan d'amélioration.",
    "profil": "OMS (plans de déploiement et de déviation CCEOP, GEV) et UNICEF (équipements de la chaîne du froid).",
    "change": "L'inventaire et le suivi GEV restent à jour entre deux évaluations.",
    "vague": "Vague 3",
    "disponible": false
  },
  {
    "code": "A7",
    "titre": "Données et évaluation",
    "fonctionnement": "Les analyses se font à la demande, souvent juste avant une réunion ou une mission. Les contrôles de qualité des données suivent un rythme irrégulier. Pour une enquête de couverture, une équipe externe conçoit le protocole, forme les enquêteurs, analyse les données et rédige le rapport.",
    "taches": [
      {
        "tache": "Contrôles de qualité mensuels",
        "habituel": "Irréguliers",
        "avec_agent": "L'agent applique chaque mois les contrôles (complétude, promptitude, valeurs aberrantes, couvertures au-dessus de 100 %, abandons négatifs) et produit un rapport par district",
        "niveau": "Remplace"
      },
      {
        "tache": "Triangulation DHIS2, E-SIGL et microplans",
        "habituel": "Ponctuelle",
        "avec_agent": "L'agent croise les trois sources et classe les écarts par gravité",
        "niveau": "Remplace"
      },
      {
        "tache": "Analyses sur demande",
        "habituel": "Analyste",
        "avec_agent": "L'agent répond aux questions de la coordination et des CRI, avec graphiques et sources",
        "niveau": "Remplace"
      },
      {
        "tache": "Bulletin trimestriel de performance",
        "habituel": "Rédaction",
        "avec_agent": "L'agent produit le bulletin au format validé",
        "niveau": "Remplace"
      },
      {
        "tache": "Enquêtes de couverture et sondages",
        "habituel": "Équipe externe",
        "avec_agent": "L'agent rédige le protocole et les outils selon les directives OMS, prépare le plan d'analyse et le rapport",
        "niveau": "Augmente"
      },
      {
        "tache": "Plan de sondage, supervision terrain, validation statistique, interprétation",
        "habituel": "Statisticiens, coordination",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Coordination nationale, section suivi-évaluation, CRI, CDI, partenaires lors des revues.",
    "sources": [
      "DHIS2, E-SIGL, microplans",
      "Estimations WUENIC, EDS 2024-2025, ENCV 2024",
      "Rapports de supervision et rapport du sondage communautaire 2025"
    ],
    "livrables": [
      "Rapport mensuel de qualité des données par district",
      "Bulletin trimestriel de performance",
      "Analyses à la demande",
      "Protocole, outils et rapport d'enquête"
    ],
    "skills": "S12 Contrôles de qualité des données ; S13 Bulletin de performance ; S14 Enquête de couverture ; S23 Population opérationnelle.",
    "garde_fous": [
      "L'agent rappelle que WUENIC fournit une estimation nationale ; la lecture par district passe par les cibles ajustées de l'analyse situationnelle FPP",
      "L'agent distingue toujours couverture administrative et couverture d'enquête"
    ],
    "prerequis": [
      "Accès en lecture à DHIS2 par API"
    ],
    "mission": "Plan d'amélioration de la qualité des données, analyses de données, bulletins de performance, enquêtes de couverture et sondages.",
    "profil": "OMS pour le plan d'amélioration de la qualité des données ; analystes des partenaires et EG ; statisticiens pour les enquêtes.",
    "change": "Les contrôles de qualité deviennent mensuels. L'équipe d'enquête reçoit protocole et outils prêts.",
    "vague": "Vague 1",
    "disponible": false
  },
  {
    "code": "A8",
    "titre": "Surveillance des MEV",
    "fonctionnement": "Les épidémiologistes compilent les données hebdomadaires, calculent les indicateurs de performance et rédigent le bulletin. Pour une MAPI grave, une équipe investigue et le comité national statue sur l'imputabilité.",
    "taches": [
      {
        "tache": "Indicateurs hebdomadaires de surveillance",
        "habituel": "Compilation manuelle",
        "avec_agent": "L'agent calcule les indicateurs par district et signale ceux qui passent sous le seuil",
        "niveau": "Remplace"
      },
      {
        "tache": "Bulletin épidémiologique",
        "habituel": "Rédaction",
        "avec_agent": "L'agent produit le bulletin au format validé",
        "niveau": "Remplace"
      },
      {
        "tache": "Détection des signaux",
        "habituel": "Lecture des tendances",
        "avec_agent": "L'agent signale les hausses inhabituelles et les silences de notification",
        "niveau": "Augmente"
      },
      {
        "tache": "Dossier d'investigation MAPI",
        "habituel": "Constitution manuelle",
        "avec_agent": "L'agent structure le dossier et vérifie sa complétude",
        "niveau": "Augmente"
      },
      {
        "tache": "Investigation, riposte, imputabilité",
        "habituel": "Équipes d'investigation, comité national",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Point focal surveillance du PEV, épidémiologistes, CRI, CDI.",
    "sources": [
      "Données de surveillance des MEV (circuit et format à confirmer)",
      "Normes de surveillance et procédures MAPI",
      "Rapports d'investigation"
    ],
    "livrables": [
      "Tableau hebdomadaire des indicateurs",
      "Bulletin épidémiologique",
      "Liste des signaux à vérifier",
      "Dossiers MAPI structurés"
    ],
    "skills": "S15 Bulletin de surveillance ; S16 Dossier MAPI et communication de crise.",
    "garde_fous": [
      "L'agent ne se prononce jamais sur l'imputabilité d'une MAPI",
      "Les signaux sont présentés comme des alertes à vérifier, pas comme des conclusions"
    ],
    "prerequis": [
      "Détenteur des données de surveillance et format d'accès",
      "Part du financement Gavi dans l'appui à la surveillance, qui relève pour beaucoup d'autres sources"
    ],
    "mission": "Analyse des données de surveillance des maladies évitables par la vaccination, bulletin épidémiologique, appui à la constitution des dossiers MAPI.",
    "profil": "OMS (surveillance des MEV, MAPI) ; partenaire cité « Mc King » pour la surveillance dans le livrable gouvernance EG (nom à vérifier).",
    "change": "Les indicateurs et le bulletin sortent chaque semaine. L'épidémiologiste se consacre aux signaux et à l'investigation.",
    "vague": "Vague 3",
    "disponible": false
  },
  {
    "code": "A9",
    "titre": "Communication et génération de la demande",
    "fonctionnement": "Le consultant fait une revue documentaire, conduit des entretiens, rédige le plan et son budget. L'étude qualitative mobilise une équipe de terrain, puis une longue phase de transcription, de codage et d'analyse. Des agences conçoivent les supports, qui sont ensuite pré-testés.",
    "taches": [
      {
        "tache": "Plan intégré de communication",
        "habituel": "Consultant international",
        "avec_agent": "L'agent rédige le plan à partir des données disponibles : publics, objectifs, canaux, chronogramme, budget",
        "niveau": "Remplace"
      },
      {
        "tache": "Guides de l'étude qualitative",
        "habituel": "Consultant",
        "avec_agent": "L'agent rédige les guides d'entretien et de groupes de discussion",
        "niveau": "Remplace"
      },
      {
        "tache": "Codage et analyse qualitative",
        "habituel": "Phase longue, manuelle",
        "avec_agent": "L'agent code les transcriptions selon une grille validée et produit la synthèse",
        "niveau": "Augmente"
      },
      {
        "tache": "Indicateurs de suivi de la communication",
        "habituel": "Binôme de consultants",
        "avec_agent": "L'agent propose le cadre et les fiches indicateurs compatibles avec DHIS2",
        "niveau": "Augmente"
      },
      {
        "tache": "Messages et supports",
        "habituel": "Agences",
        "avec_agent": "L'agent produit les premiers jets par public et par canal, y compris en langues locales",
        "niveau": "Augmente"
      },
      {
        "tache": "Terrain, pré-tests, validation culturelle et linguistique",
        "habituel": "Équipe de terrain, locuteurs",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Section communication du PEV, partenaires de la génération de la demande, OSC.",
    "sources": [
      "ENCV 2024 : raisons de non-vaccination, connaissances des mères et gardiens",
      "Sondage communautaire 2025 et rapports de supervision",
      "Cartographie des OSC, PSNSC 2023-2027"
    ],
    "livrables": [
      "Plan intégré de communication",
      "Guides d'entretien et synthèse qualitative",
      "Cadre d'indicateurs de la communication",
      "Premiers jets de messages et de supports"
    ],
    "skills": "S16 Dossier MAPI et communication de crise ; S17 Plan de communication ; S18 Codage qualitatif ; S19 Messages et supports.",
    "garde_fous": [
      "Aucun message en langue locale n'est diffusé sans vérification par un locuteur",
      "Aucune donnée personnelle des répondants dans les transcriptions traitées"
    ],
    "prerequis": [
      "Grille de codage validée par la section communication",
      "Agents de continuité au sein du PEV",
      "Ces trois agents ne remplacent pas une mission d'expertise internationale. Ils gardent en fonctionnement des processus que l'assistance technique a mis en place, et l'agent A12 existe déjà dans Omnia."
    ],
    "mission": "Plan intégré de communication, étude socio-anthropologique sur les déterminants de la demande, indicateurs de suivi de la communication, supports et messages.",
    "profil": "Consultant international pour le plan intégré ; binôme international et national socio-anthropologue ; binôme international et national pour les indicateurs ; UNICEF et OIM pour la génération de la demande.",
    "change": "Le plan et l'analyse qualitative se produisent en interne. L'expertise externe se concentre sur le terrain et la validation culturelle.",
    "vague": "Vague 2",
    "disponible": false
  },
  {
    "code": "A10",
    "titre": "Secrétariat des instances",
    "fonctionnement": "Le secrétariat prépare les dossiers de session, rédige les procès-verbaux et suit les recommandations. Le suivi s'interrompt dès que la session suivante est reportée, comme pour le CTC en 2025.",
    "taches": [
      {
        "tache": "Dossiers de session",
        "habituel": "Préparation manuelle",
        "avec_agent": "L'agent assemble le dossier à partir des données et du statut des recommandations",
        "niveau": "Remplace"
      },
      {
        "tache": "Procès-verbaux",
        "habituel": "Rédaction après la session",
        "avec_agent": "L'agent produit le projet de PV à partir des notes ou de l'enregistrement",
        "niveau": "Remplace"
      },
      {
        "tache": "Suivi des recommandations",
        "habituel": "Tableur, relances irrégulières",
        "avec_agent": "L'agent tient à jour le cockpit (six statuts) et prépare les relances",
        "niveau": "Remplace"
      },
      {
        "tache": "Animation, arbitrages",
        "habituel": "Coordination",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Secrétariat de coordination, coordination nationale, CRI pour les CTRS.",
    "sources": [
      "PV et recommandations des instances",
      "Cockpit de pilotage",
      "Bulletins de performance"
    ],
    "livrables": [
      "Dossiers de session",
      "Projets de PV",
      "État des recommandations"
    ],
    "skills": "S13 Bulletin de performance ; S20 Dossier de session et PV.",
    "garde_fous": [
      "Aucun PV diffusé sans validation du président de séance"
    ],
    "prerequis": [
      "Articulation avec le module cockpit en cours de développement"
    ],
    "mission": "Appui au leadership, au management et à la coordination, et au fonctionnement des instances : CCIA, CTC, GTCV, CTRS, CTPS.",
    "profil": "Expertise EG (appui LMC).",
    "change": "Le suivi des recommandations continue même quand une session est reportée.",
    "vague": "Vague 1",
    "disponible": false
  },
  {
    "code": "A11",
    "titre": "Tuteur PEV",
    "fonctionnement": "Une session de formation se tient en présentiel. Après la session, les participants n'ont plus d'interlocuteur pour leurs questions, et les acquis ne sont pas suivis.",
    "taches": [
      {
        "tache": "Modules et supports de formation",
        "habituel": "Consultant",
        "avec_agent": "L'agent produit les modules à partir des normes et des outils du PEV",
        "niveau": "Remplace"
      },
      {
        "tache": "Questions après la formation",
        "habituel": "Sans réponse",
        "avec_agent": "L'agent répond à partir des documents validés",
        "niveau": "Remplace"
      },
      {
        "tache": "Évaluation des acquis",
        "habituel": "Ponctuelle",
        "avec_agent": "L'agent propose des évaluations et suit les résultats par participant",
        "niveau": "Augmente"
      },
      {
        "tache": "Animation en présentiel, coaching",
        "habituel": "Formateurs",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Cadres du PEV, CRI, CDI, agents de santé.",
    "sources": [
      "Normes et procédures du PEV (2021)",
      "Guides des outils numériques du PEV",
      "Manuels de procédures"
    ],
    "livrables": [
      "Modules",
      "Foire aux questions",
      "Évaluations et suivi des acquis"
    ],
    "skills": "S22 Fiches de poste, TDR et plans de formation.",
    "garde_fous": [
      "Réponses limitées aux documents validés, avec citation"
    ],
    "prerequis": [
      "Liste des formations planifiées en 2026-2027"
    ],
    "mission": "Formation et renforcement des capacités des cadres centraux, régionaux et de district.",
    "profil": "Consultant national, OMS, EG.",
    "change": "Les participants gardent un interlocuteur après la formation.",
    "vague": "Vague 2",
    "disponible": false
  },
  {
    "code": "A12",
    "titre": "Rédaction institutionnelle",
    "fonctionnement": "Les documents institutionnels passent par quelques cadres déjà surchargés ou par des consultants.",
    "taches": [
      {
        "tache": "Premiers jets de documents",
        "habituel": "Cadres ou consultants",
        "avec_agent": "Déjà couvert par Omnia",
        "niveau": "Remplace"
      },
      {
        "tache": "Recherche documentaire",
        "habituel": "Recherche manuelle",
        "avec_agent": "Déjà couvert par Omnia",
        "niveau": "Remplace"
      },
      {
        "tache": "Validation politique",
        "habituel": "Coordination, cabinet",
        "avec_agent": "Hors champ de l'agent",
        "niveau": "Humain"
      }
    ],
    "utilisateurs": "Coordination nationale et sections du PEV.",
    "sources": [
      "Base documentaire du PEV indexée dans Omnia"
    ],
    "livrables": [
      "Documents institutionnels"
    ],
    "skills": "",
    "garde_fous": [
      "Pas de nouveau développement : les autres agents réutilisent son index documentaire"
    ],
    "prerequis": [],
    "mission": "Production de notes, rapports, discours, communiqués et plaidoyers ; résumé de documents volumineux ; recherche dans la base documentaire du PEV.",
    "profil": "Consultants en rédaction, EG.",
    "change": "Aucun changement : agent existant, réutilisé par les autres.",
    "vague": "Existant",
    "disponible": false
  }
];
